import { Injectable, Injector, NgModuleRef, Type, ViewContainerRef, createNgModule } from '@angular/core';
import type { NavShellComponent } from './nav-shell.component';
import type { NavShellModule } from './nav-shell.module';

export interface MountedNavComponent {
  destroy(): void;
}

interface NavShellExports {
  NavShellModule: Type<NavShellModule>;
  NavShellComponent: Type<NavShellComponent>;
}

@Injectable({ providedIn: 'root' })
export class NavShellLoader {
  private pending?: Promise<NavShellExports>;

  mountNavShell(host: ViewContainerRef, injector: Injector): Promise<MountedNavComponent> {
    return this.mount(host, injector, (exports) => exports.NavShellComponent);
  }

  private load(): Promise<NavShellExports> {
    if (!this.pending) {
      this.pending = Promise.all([
        import('./nav-shell.module'),
        import('./nav-shell.component'),
      ]).then(([moduleExports, shellExports]) => ({
        NavShellModule: moduleExports.NavShellModule,
        NavShellComponent: shellExports.NavShellComponent,
      }));
    }
    return this.pending;
  }

  private async mount(
    host: ViewContainerRef,
    injector: Injector,
    pick: (exports: NavShellExports) => Type<unknown>,
  ): Promise<MountedNavComponent> {
    const exports = await this.load();
    // The component needs NavShellModule's own injector for LucideAngularModule.pick()'s
    // LUCIDE_ICONS providers; without ngModuleRef every icon resolves to nothing.
    const moduleRef: NgModuleRef<unknown> = createNgModule(exports.NavShellModule, injector);
    const componentRef = host.createComponent(pick(exports), { ngModuleRef: moduleRef });
    componentRef.changeDetectorRef.detectChanges();
    return {
      destroy: () => {
        componentRef.destroy();
        moduleRef.destroy();
      },
    };
  }
}
